/**
 * Unified pointer/gesture recognizer (mouse + touch).
 *
 * - 1 pointer  → drag (interpretation depends on `dragMode`: 'pan' or 'aim')
 * - 2 pointers → camera (midpoint pan + pinch zoom), cancels any drag
 * - quick small-strike release → tap
 * - mouse wheel → zoom at cursor
 */
export interface GestureCbs {
  onTap?(sx: number, sy: number): void;
  onDragStart?(sx: number, sy: number): void;
  /** (current, origin) in CSS px. */
  onDragMove?(sx: number, sy: number, ox: number, oy: number): void;
  onDragEnd?(sx: number, sy: number, ox: number, oy: number, cancelled: boolean): void;
  /** Screen-space delta. */
  onPan?(dx: number, dy: number): void;
  /** Scale ratio since the previous pinch event, anchored at (cx, cy). */
  onPinch?(scale: number, cx: number, cy: number): void;
  onWheel?(deltaY: number, sx: number, sy: number): void;
}

interface Ptr {
  x: number;
  y: number;
  ox: number;
  oy: number;
  t: number;
}

const TAP_MAX_TRAVEL = 8;
const TAP_MAX_MS = 260;

export class Gestures {
  dragMode: 'pan' | 'aim' = 'pan';

  private el: HTMLElement;
  private cbs: GestureCbs;
  private pointers = new Map<number, Ptr>();
  private mode: 'idle' | 'drag' | 'camera' = 'idle';
  private moved = false;
  private lastMidX = 0;
  private lastMidY = 0;
  private lastPinchDist = 0;

  constructor(el: HTMLElement, cbs: GestureCbs) {
    this.el = el;
    this.cbs = cbs;
    el.addEventListener('pointerdown', this.onDown);
    el.addEventListener('pointermove', this.onMove);
    el.addEventListener('pointerup', this.onUp);
    el.addEventListener('pointercancel', this.onUp);
    el.addEventListener('wheel', this.onWheel, { passive: false });
    el.addEventListener('contextmenu', this.onContext);
  }

  dispose(): void {
    this.el.removeEventListener('pointerdown', this.onDown);
    this.el.removeEventListener('pointermove', this.onMove);
    this.el.removeEventListener('pointerup', this.onUp);
    this.el.removeEventListener('pointercancel', this.onUp);
    this.el.removeEventListener('wheel', this.onWheel);
    this.el.removeEventListener('contextmenu', this.onContext);
  }

  private pos(e: { clientX: number; clientY: number }): { x: number; y: number } {
    const r = this.el.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }

  private onContext = (e: Event): void => e.preventDefault();

  private onDown = (e: PointerEvent): void => {
    if (this.pointers.size >= 2) return;
    const { x, y } = this.pos(e);
    this.el.setPointerCapture(e.pointerId);
    this.pointers.set(e.pointerId, { x, y, ox: x, oy: y, t: performance.now() });

    if (this.pointers.size === 2) {
      // Second finger → camera mode; cancel any in-flight drag.
      if (this.mode === 'drag') {
        const first = [...this.pointers.values()][0];
        this.cbs.onDragEnd?.(first.x, first.y, first.ox, first.oy, true);
      }
      this.mode = 'camera';
      this.moved = false;
      const pts = [...this.pointers.values()];
      this.lastMidX = (pts[0].x + pts[1].x) / 2;
      this.lastMidY = (pts[0].y + pts[1].y) / 2;
      this.lastPinchDist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      return;
    }

    // Middle-mouse always pans; otherwise follow dragMode.
    const forcePan = e.button === 1;
    this.mode = 'drag';
    this.moved = false;
    const forcePanMode = forcePan;
    this.dragWasPan = forcePanMode || this.dragMode === 'pan';
    this.cbs.onDragStart?.(x, y);
  };

  private dragWasPan = false;

  private onMove = (e: PointerEvent): void => {
    const p = this.pointers.get(e.pointerId);
    if (!p) return;
    const { x, y } = this.pos(e);
    const prevX = p.x;
    const prevY = p.y;
    p.x = x;
    p.y = y;

    if (this.mode === 'camera') {
      if (this.pointers.size < 2) return;
      const pts = [...this.pointers.values()];
      const midX = (pts[0].x + pts[1].x) / 2;
      const midY = (pts[0].y + pts[1].y) / 2;
      this.cbs.onPan?.(midX - this.lastMidX, midY - this.lastMidY);
      const d = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      if (this.lastPinchDist > 4 && d > 4) {
        this.cbs.onPinch?.(d / this.lastPinchDist, midX, midY);
      }
      this.lastMidX = midX;
      this.lastMidY = midY;
      this.lastPinchDist = d;
      return;
    }

    if (this.mode === 'drag') {
      const travel = Math.hypot(x - p.ox, y - p.oy);
      if (!this.moved && travel > TAP_MAX_TRAVEL) this.moved = true;
      if (this.moved) {
        if (this.dragWasPan) {
          this.cbs.onPan?.(x - prevX, y - prevY);
        } else {
          this.cbs.onDragMove?.(x, y, p.ox, p.oy);
        }
      }
    }
  };

  private onUp = (e: PointerEvent): void => {
    const p = this.pointers.get(e.pointerId);
    if (!p) return;
    this.pointers.delete(e.pointerId);

    if (this.mode === 'drag') {
      const dt = performance.now() - p.t;
      if (!this.moved && dt < TAP_MAX_MS) {
        this.cbs.onTap?.(p.x, p.y);
      } else if (this.moved) {
        this.cbs.onDragEnd?.(p.x, p.y, p.ox, p.oy, false);
      }
    }

    if (this.pointers.size === 0) {
      this.mode = 'idle';
    } else if (this.pointers.size === 1 && this.mode === 'camera') {
      // One finger left after pinch: go idle until it lifts (no surprise drags).
      this.mode = 'idle';
    }
  };

  private onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    const { x, y } = this.pos(e);
    this.cbs.onWheel?.(e.deltaY, x, y);
  };
}
